import { useParams, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Loading } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconArrowBack } from '@tabler/icons-react'
import { useClient } from '@/stores/useClient.ts'
import { BasketItem } from '@/models/shop.ts'
import { AlbumFile } from '@/models/albumFile.ts'
import { useDrives } from '@/stores/useDrives.ts'
import { FilesProvider, useFilesContext } from '@/contexts/filesContext.tsx'
import { Album } from '@/models/album.ts'
import { AlbumFileType } from '@/models/albumFileType.ts'
import AlbumSelection from '@/components/AlbumSelection.tsx'
import UnifiedImageSelectionView from '@/components/UnifiedImageSelectionView.tsx'

export default function BasketEditSelectPhotosPage() {
  const { driveId, orderItemId } = useParams<{ driveId: string; orderItemId: string }>()
  const navigate = useNavigate()
  const { getBasketItemDetail, updateBasketItemDetails } = useClient()

  const [basketItem, setBasketItem] = useState<BasketItem | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedImages, setSelectedImages] = useState<AlbumFile[]>([])
  const [isProcessing, setIsProcessing] = useState(false)

  useEffect(() => {
    const fetchOrderItem = async () => {
      if (!orderItemId) return

      try {
        setIsLoading(true)
        const response = await getBasketItemDetail(orderItemId)
        const item: BasketItem = response.data
        setBasketItem(item)

        // Initialize selected images from playlist
        if (item.playlist?.files) {
          const images = item.playlist.files.filter(file => file.file).map(connection => connection.file!)
          setSelectedImages(images)
        }
      } catch (error) {
        console.error('Error fetching order item:', error)
        setError('Fehler beim Laden des Warenkorb-Artikels')
      } finally {
        setIsLoading(false)
      }
    }

    fetchOrderItem()
  }, [orderItemId, getBasketItemDetail])

  const handleSaveAndContinue = async (newSelectedImages: AlbumFile[]) => {
    if (!basketItem) return

    setIsProcessing(true)

    try {
      const fileIds = newSelectedImages.map(img => img.id)

      await updateBasketItemDetails(orderItemId!, {
        files: fileIds,
        update_existing: true
      })

      // Navigate to crop page
      navigate(`/d/${driveId}/shop/basket/edit/${orderItemId}/crop-photos`)
    } catch (error) {
      console.error('Error updating order item:', error)
      alert('Fehler beim Speichern der Bildauswahl')
    } finally {
      setIsProcessing(false)
    }
  }

  const goBackToEdit = () => {
    navigate(`/d/${driveId}/shop/basket/edit/${orderItemId}`)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading color="primary" />
      </div>
    )
  }

  if (error || !basketItem) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <h1 className="text-2xl font-bold text-error">
            {error || 'Warenkorb-Artikel nicht gefunden'}
          </h1>
          <Button color="primary" variant="outline" onClick={goBackToEdit}>
            <IconArrowBack size={20} />
            Zurück
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Product info */}
      <div className="bg-base-200 p-6 rounded-lg mb-6">
        <h2 className="text-lg font-bold">{basketItem.product.title}</h2>
        {basketItem.variation && (
          <p className="text-sm text-primary">{basketItem.variation.title_suffix}</p>
        )}
      </div>

      {/* Photo selection component */}
      <PhotoSelectionComponent
        minImageCount={basketItem.product.min_image_count}
        maxImageCount={basketItem.product.max_image_count}
        onContinue={handleSaveAndContinue}
        initialSelectedImages={selectedImages}
        isProcessing={isProcessing}
        onBack={goBackToEdit}
      />
    </div>
  )
}

function PhotoSelectionComponent({
  minImageCount,
  maxImageCount,
  onContinue,
  initialSelectedImages = [],
  isProcessing = false,
  onBack
}: {
  minImageCount: number | null
  maxImageCount: number | null
  onContinue: (selectedImages: AlbumFile[]) => void
  initialSelectedImages?: AlbumFile[]
  isProcessing?: boolean
  onBack: () => void
}) {
  const { currentDrive } = useDrives()
  const { currentAlbum: currentOuterAlbum } = useFilesContext()!
  const [currentAlbum, setCurrentAlbum] = useState(currentOuterAlbum!)
  const [selectedImages, setSelectedImages] = useState<AlbumFile[]>(initialSelectedImages)

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-base-200 p-4 rounded-lg">
        <div className="mb-4">
          <AlbumSelection
            drive={currentDrive!}
            selectedAlbum={currentAlbum!}
            setSelectedAlbum={(album: Album) => setCurrentAlbum(album)}
          />
        </div>

        <FilesProvider albumId={currentAlbum.id} filesFilter={{media: AlbumFileType.IMAGE}}>
          <UnifiedImageSelectionView
            selectedImages={selectedImages}
            setSelectedImages={setSelectedImages}
            minImageCount={minImageCount}
            maxImageCount={maxImageCount}
            initialSelectedImages={initialSelectedImages}
            onContinue={() => onContinue(selectedImages)}
            onBack={onBack}
            isProcessing={isProcessing}
          />
        </FilesProvider>
      </div>
    </div>
  )
}

